/**
 * Derivations of DPP server addresses from the configurator's existing
 * avery_dpo endpoint settings. Sampling a configured endpoint URL instead
 * of duplicating the host in config keeps every consumer env-correct
 * (test vs production DPP) automatically.
 */

const sampleEndpoint = (dpoSettings) =>
  dpoSettings?.design_with_quote_endpoints?.standard ||
  dpoSettings?.canvas_with_quote_endpoints?.standard ||
  dpoSettings?.qau_widget_endpoint ||
  '';

/**
 * The per-country DPO base (".../dpp/public/v3/dpo/") for building DPO
 * entry and hand-off URLs. Returns null when no usable endpoint is present.
 */
export const deriveDpoBase = (dpoSettings) => {
  const sample = sampleEndpoint(dpoSettings);
  const cut = sample.indexOf('entry/');
  const widgetCut = sample.indexOf('widget/');
  const at = cut !== -1 ? cut : widgetCut;
  return at > 0 ? sample.slice(0, at) : null;
};

/**
 * The bare DPP origin (e.g. "https://test.print.avery.com") for API paths
 * outside the v3 dpo base, such as the v5 snapshot quote update. Returns
 * null when no usable endpoint is present.
 */
export const deriveDppOrigin = (dpoSettings) => {
  try {
    return new URL(sampleEndpoint(dpoSettings)).origin;
  } catch (e) {
    return null;
  }
};

/**
 * Pushes the given price-matrix quote onto a freshly minted order snapshot.
 *
 * A DPP project only receives a quote at creation (SDK init); options the
 * customer changes afterwards (material, varnish) update the price matrix
 * but never reach DPP — and the production press files (white-underprint
 * layer, die-cut spot color) are generated from the snapshot's quote. This
 * posts to the same public DPP endpoint Magento's own quote-update helper
 * uses, so the snapshot reflects the ordered configuration.
 *
 * Failure-tolerant by design: every skip or failure is logged with a
 * greppable [DPLE] prefix and the caller proceeds. Without the push the
 * snapshot keeps its creation-time quote — the pre-existing exposure —
 * and blocking a sale on a DPP hiccup would be worse. The request is
 * bounded to 10s so a stalling DPP can never park the customer on a
 * disabled cart button.
 */
export const pushSnapshotQuote = async ({
  dpoSettings,
  quote,
  exitResult,
  consumer,
  fallbackDeploymentId,
}) => {
  const origin = deriveDppOrigin(dpoSettings);
  // Current SDK builds return deploymentId and quote on exitToOrder, but
  // neither field is documented — fall back to the deployment the SDK was
  // initialised with so a leaner build can't silently disable the push.
  const deploymentId = exitResult?.deploymentId || fallbackDeploymentId;
  const snapshotId = exitResult?.projectSnapshotId;
  if (!origin || !quote || !snapshotId || !deploymentId) {
    const missing = [
      !origin && 'origin',
      !quote && 'quote',
      !snapshotId && 'snapshot id',
      !deploymentId && 'deployment',
    ].filter(Boolean).join(', ');
    // eslint-disable-next-line no-console
    console.error(`[DPLE] snapshot quote push skipped (missing: ${missing}); the order keeps the creation-time quote.`);
    return;
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    const body = new FormData();
    body.append('quote', JSON.stringify({
      ...quote,
      // DPP normalises the stored profile (e.g. 'sticker' → 'sheet') and
      // rejects updates whose profile differs from the stored one — echo
      // the profile the snapshot actually carries when the SDK exposes it.
      profile: exitResult.quote?.profile ?? quote.profile,
      projectSnapshotId: snapshotId,
    }));
    const query = new URLSearchParams({deploymentId, consumer: consumer ?? ''});
    const res = await fetch(
      `${origin}/dpp/public/v5/snapshots/${encodeURIComponent(snapshotId)}/order-submitted?${query}`,
      {method: 'POST', body, signal: controller.signal},
    );
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.error(`[DPLE] snapshot quote push failed (${res.status}); the order keeps the creation-time quote.`);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[DPLE] snapshot quote push failed; the order keeps the creation-time quote.', error);
  } finally {
    clearTimeout(timeout);
  }
};
